<script setup lang="ts">
import FinestAmountInput from '@/components/FinestAmountInput.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCalcRow from '@/components/FinestCalcRow.vue'
import FinestInputNumber from '@/components/FinestInputNumber.vue'
import { useSyncedModel } from '@/composables/useSyncedModel'
import { useEstateObjectUpdate } from '@/pages/investors/portfolio/useEstateObjectUpdate'
import { counterpart, type AmountUnit } from '@/utils/amount-unit'
import { userLeftTheField } from '@/utils/field-leave'
import { useFieldStates, type FieldStateMap } from '@/utils/field-state'
import {
  additionalPurchaseCostKeys,
  additionalPurchaseCosts,
  modernizationCosts as sumModernizations,
} from '@/utils/investment'
import { toNumber } from '@/utils/number'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import ModernizationData = App.Data.EstateObject.ModernizationData

const amountKeys = additionalPurchaseCostKeys

type AmountKey = (typeof amountKeys)[number]

type UnitKey = `${AmountKey}_unit`

type InvestmentModel = Record<'purchase_price' | 'equity' | AmountKey, number | null> &
  Record<UnitKey, AmountUnit | null>

const props = defineProps<{
  estateObject: EstateObjectData
  fieldStates: FieldStateMap
  modernizations: ModernizationData[]
  loansTotal: number
  loansCount: number
}>()

const emit = defineEmits<{
  openModernizations: []
  addFinancing: []
}>()

const { t, n } = useI18n({ useScope: 'local' })

const unitKeyOf = (key: AmountKey): UnitKey => `${key}_unit`

const persistedUnit = (key: AmountKey): AmountUnit => props.estateObject[unitKeyOf(key)] ?? 'percent'

const seedModel = (): InvestmentModel => ({
  purchase_price: toNumber(props.estateObject.purchase_price),
  equity: toNumber(props.estateObject.equity),
  land_register_charge: props.estateObject.land_register_charge ?? null,
  land_register_charge_unit: persistedUnit('land_register_charge'),
  broker_commission: props.estateObject.broker_commission ?? null,
  broker_commission_unit: persistedUnit('broker_commission'),
  notary_land_registry_fees: props.estateObject.notary_land_registry_fees ?? null,
  notary_land_registry_fees_unit: persistedUnit('notary_land_registry_fees'),
})

const { model: draft, confirm } = useSyncedModel(seedModel)

const update = useEstateObjectUpdate(props.estateObject.id ?? '', confirm)

const unitOf = (key: AmountKey): AmountUnit => draft.value[unitKeyOf(key)] ?? 'percent'

const amounts = computed(
  () => Object.fromEntries(amountKeys.map((key) => [key, draft.value[key]])) as Record<AmountKey, number | null>,
)

const units = computed(
  () => Object.fromEntries(amountKeys.map((key) => [key, unitOf(key)])) as Record<AmountKey, AmountUnit>,
)

const { presentationOf } = useFieldStates(() => props.fieldStates)

const placeholderFor = (key: string, value: number | null): string | undefined => {
  if (presentationOf(key).state === 'reading') {
    return t('reading')
  }

  return value === null ? t('addValue') : undefined
}

const additionalCosts = computed(() => additionalPurchaseCosts(draft.value.purchase_price, amounts.value, units.value))

const modernizationCosts = computed(() => sumModernizations(props.modernizations))

const totalInvestment = computed(
  () => (draft.value.purchase_price ?? 0) + additionalCosts.value + modernizationCosts.value,
)

const difference = computed(() => totalInvestment.value - (draft.value.equity ?? 0) - props.loansTotal)

const isCovered = computed(() => Math.abs(difference.value) < 0.5)

const spaceSize = computed(
  () => (toNumber(props.estateObject.living_space_size) ?? 0) + (toNumber(props.estateObject.business_space_size) ?? 0),
)

const euro = (value: number | null): string =>
  value === null ? '—' : n(value, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

const share = (value: number | null): string | null =>
  value === null || totalInvestment.value <= 0
    ? null
    : n(value / totalInvestment.value, { style: 'percent', maximumFractionDigits: 1 })

const conversionOf = (key: AmountKey): string | null => {
  const other = counterpart(draft.value[key], unitOf(key), draft.value.purchase_price)

  if (other === null) {
    return null
  }

  return other.unit === 'euro' ? `≈ ${euro(other.value)}` : `≈ ${n(other.value, { maximumFractionDigits: 2 })} %`
}

const pricePerSquareMeter = computed(() => {
  const purchasePrice = draft.value.purchase_price

  if (purchasePrice === null || spaceSize.value <= 0) {
    return null
  }

  return t('perSquareMeter', { amount: n(purchasePrice / spaceSize.value, { maximumFractionDigits: 0 }) })
})

const financingHint = computed(() =>
  difference.value > 0 ? t('hints.gap') : t('hints.surplus', { amount: euro(-difference.value) }),
)

const uncovered = computed(() => (isCovered.value ? 0 : Math.max(difference.value, 0)))

const coverageBase = computed(() => Math.max(totalInvestment.value, (draft.value.equity ?? 0) + props.loansTotal))

const coverageSegments = computed(() =>
  [
    { key: 'equity', label: t('fields.equity'), amount: draft.value.equity ?? 0, color: 'bg-brand-blue-1' },
    { key: 'loans', label: t('fields.loans'), amount: props.loansTotal, color: 'bg-brand-teal' },
    { key: 'gap', label: t('coverage.gap'), amount: uncovered.value, color: 'coverage-gap' },
  ]
    .filter((segment) => segment.amount > 0)
    .map((segment) => ({
      ...segment,
      width: `${(segment.amount / coverageBase.value) * 100}%`,
      share: share(segment.amount),
    })),
)

const coverageLabel = computed(() =>
  coverageSegments.value.map((segment) => `${segment.label} ${segment.share ?? euro(segment.amount)}`).join(', '),
)

function commitPurchasePrice(): void {
  if (draft.value.purchase_price === toNumber(props.estateObject.purchase_price)) {
    return
  }

  update({ purchase_price: draft.value.purchase_price })
}

function commitEquity(): void {
  if (draft.value.equity === toNumber(props.estateObject.equity)) {
    return
  }

  update({ equity: draft.value.equity })
}

function commitAmount(key: AmountKey): void {
  const unchanged = draft.value[key] === (props.estateObject[key] ?? null) && unitOf(key) === persistedUnit(key)

  if (unchanged) {
    return
  }

  update({ [key]: draft.value[key], [unitKeyOf(key)]: unitOf(key) })
}
</script>

<template>
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-5">
    <section class="fh-card @container min-w-0 p-6 lg:col-span-3" style="--calc-value: 220px">
      <h2 class="pb-2">{{ t('investment.title') }}</h2>

      <FinestCalcRow
        :label="t('fields.purchase_price')"
        :required="presentationOf('purchase_price').required"
        :conversion="pricePerSquareMeter">
        <FinestInputNumber
          v-model="draft.purchase_price"
          mode="currency"
          :max-fraction-digits="0"
          :state="presentationOf('purchase_price').state"
          :placeholder="placeholderFor('purchase_price', draft.purchase_price)"
          :aria-label="t('fields.purchase_price')"
          class="w-full"
          @keydown.enter.prevent="commitPurchasePrice"
          @blur="userLeftTheField() && commitPurchasePrice()" />
      </FinestCalcRow>

      <FinestCalcRow variant="group" sign="+" :label="t('groups.additionalCosts')" :value="euro(additionalCosts)" />

      <FinestCalcRow
        v-for="key in amountKeys"
        :key="key"
        indent
        :label="t(`fields.${key}`)"
        :required="presentationOf(key).required"
        :conversion="conversionOf(key)">
        <FinestAmountInput
          v-model="draft[key]"
          v-model:unit="draft[unitKeyOf(key)]"
          :reference-value="draft.purchase_price"
          :state="presentationOf(key).state"
          :placeholder="placeholderFor(key, draft[key])"
          :aria-label="t(`fields.${key}`)"
          class="w-full"
          @unit-change="commitAmount(key)"
          @keydown.enter.prevent="commitAmount(key)"
          @blur="userLeftTheField() && commitAmount(key)" />
      </FinestCalcRow>

      <FinestCalcRow variant="group" sign="+" :label="t('groups.modernization')" :value="euro(modernizationCosts)" />

      <FinestCalcRow
        indent
        :label="t('fields.modernization_costs')"
        :hint="t('measures', modernizations.length)"
        :value="euro(modernizationCosts)">
        <template #sub>
          <FinestButton
            variant="ghost"
            size="sm"
            icon="pi pi-arrow-right"
            icon-pos="right"
            class="-ml-[11px]"
            :label="t('openModernizations')"
            @click="emit('openModernizations')" />
        </template>
      </FinestCalcRow>

      <FinestCalcRow variant="total" sign="=" :label="t('investment.total')" :value="euro(totalInvestment)" />
    </section>

    <section class="fh-card @container min-w-0 p-6 lg:col-span-2" style="--calc-value: 170px">
      <h2 class="pb-2">{{ t('financing.title') }}</h2>

      <template v-if="coverageSegments.length">
        <div class="bg-surface-3 flex h-3 gap-0.5 overflow-hidden rounded-full" role="img" :aria-label="coverageLabel">
          <span
            v-for="segment in coverageSegments"
            :key="segment.key"
            :class="segment.color"
            :style="{ width: segment.width }" />
        </div>

        <ul class="text-ink-2 flex flex-wrap gap-x-4 gap-y-1 pt-2 pb-1 text-xs">
          <li v-for="segment in coverageSegments" :key="segment.key" class="flex items-center gap-1.5">
            <span class="size-2.5 rounded-sm" :class="segment.color" aria-hidden="true" />
            <span>{{ segment.label }}</span>
            <span v-if="segment.share" class="text-ink-3 tabular-nums">{{ segment.share }}</span>
          </li>
        </ul>
      </template>

      <FinestCalcRow :gutter="false" :label="t('investment.total')" :value="euro(totalInvestment)" />

      <FinestCalcRow
        :gutter="false"
        :label="t('fields.equity')"
        :required="presentationOf('equity').required"
        :conversion="share(draft.equity)">
        <FinestInputNumber
          v-model="draft.equity"
          mode="currency"
          :max-fraction-digits="0"
          :state="presentationOf('equity').state"
          :placeholder="placeholderFor('equity', draft.equity)"
          :aria-label="t('fields.equity')"
          class="w-full"
          @keydown.enter.prevent="commitEquity"
          @blur="userLeftTheField() && commitEquity()" />
      </FinestCalcRow>

      <FinestCalcRow
        :gutter="false"
        :label="t('fields.loans')"
        :hint="t('loanPositions', loansCount)"
        :conversion="share(loansTotal)"
        :value="euro(loansTotal)" />

      <FinestCalcRow
        v-if="uncovered > 0"
        :gutter="false"
        variant="gap"
        :label="t('fields.uncovered')"
        :conversion="share(uncovered)"
        :value="euro(uncovered)" />

      <div
        v-if="!isCovered"
        class="border-warning/40 bg-warning-subtle text-ink-2 mt-3 flex items-start gap-3 rounded-lg border p-3 text-sm">
        <i class="pi pi-exclamation-circle text-warning mt-0.5" aria-hidden="true" />
        <div class="flex min-w-0 flex-col items-start gap-1">
          <span>{{ financingHint }}</span>
          <FinestButton
            v-if="uncovered > 0"
            variant="ghost"
            size="sm"
            icon="pi pi-arrow-right"
            icon-pos="right"
            class="-ml-[11px]"
            :label="t('addFinancing')"
            @click="emit('addFinancing')" />
        </div>
      </div>
    </section>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "investment": {
      "title": "Gesamtinvestition",
      "total": "Gesamtinvestition"
    },
    "financing": {
      "title": "Wie es finanziert wird"
    },
    "coverage": {
      "gap": "offen"
    },
    "groups": {
      "additionalCosts": "Kaufnebenkosten",
      "modernization": "Modernisierung"
    },
    "fields": {
      "purchase_price": "Kaufpreis",
      "land_register_charge": "Grunderwerbssteuer",
      "broker_commission": "Maklerprovision",
      "notary_land_registry_fees": "Notar- und Grundbuchkosten",
      "modernization_costs": "Modernisierungskosten",
      "equity": "Eigenkapital",
      "loans": "Finanzierungen",
      "uncovered": "Noch nicht finanziert"
    },
    "hints": {
      "gap": "Eigenkapital erhöhen oder eine Finanzierung anpassen.",
      "surplus": "Eigenkapital und Finanzierungen übersteigen die Gesamtinvestition um {amount}."
    },
    "measures": "Keine Maßnahmen | 1 Maßnahme | {count} Maßnahmen",
    "loanPositions": "Keine Positionen unten | 1 Position unten | {count} Positionen unten",
    "openModernizations": "Zu den Modernisierungen",
    "addFinancing": "Finanzierung hinzufügen",
    "perSquareMeter": "{amount} €/m²",
    "addValue": "+ eintragen",
    "reading": "Kommt aus Dokument"
  },
  "en": {
    "investment": {
      "title": "Total investment",
      "total": "Total investment"
    },
    "financing": {
      "title": "How it is financed"
    },
    "coverage": {
      "gap": "open"
    },
    "groups": {
      "additionalCosts": "Additional purchase costs",
      "modernization": "Modernisation"
    },
    "fields": {
      "purchase_price": "Purchase price",
      "land_register_charge": "Property transfer tax",
      "broker_commission": "Broker commission",
      "notary_land_registry_fees": "Notary & land registry fees",
      "modernization_costs": "Modernisation costs",
      "equity": "Equity",
      "loans": "Financings",
      "uncovered": "Not financed yet"
    },
    "hints": {
      "gap": "Increase the equity or adjust a financing.",
      "surplus": "Equity and financings exceed the total investment by {amount}."
    },
    "measures": "No measures | 1 measure | {count} measures",
    "loanPositions": "No positions below | 1 position below | {count} positions below",
    "openModernizations": "Go to modernisations",
    "addFinancing": "Add financing",
    "perSquareMeter": "{amount} €/m²",
    "addValue": "+ add",
    "reading": "Comes from a document"
  }
}
</i18n>
